/**
 * The Workflows page (`docs/proposals/workflows.md`, "Listing"): every workflow
 * the agent lists, as one list of cards with a category filter and a sort.
 *
 * The cards are the agent's (`GET /workflows`): their text, their state — open,
 * or Coming soon — and whether one is ready to start on this machine (a Revit
 * connected). A beta first asks for opt-in; enabled cards require a selected
 * project before entering the workflow's view. Host connection is checked inside
 * the workflow, so the user can enter before connecting Revit. A workflow's
 * conversation is about a project like any other, so without one selected the card says so instead
 * of opening a view that could not start. An agent from before the list route
 * gets one line: update the desktop app.
 */
import { useState } from "react";
import type { WorkflowCategory } from "../../../shared/workflow-listing";
import { useLocale } from "../i18n/LocaleProvider";
import { describeAgent, type AgentTarget } from "../lib/agentClient";
import { workflowText } from "../lib/workflowList";
import { DEFAULT_WORKFLOW_SORT, WORKFLOW_CATEGORIES, listWorkflows, openWorkflow, type WorkflowSort } from "../workflows";
import { Modal } from "../components/settings/Modal";
import type { WorkflowListState } from "../workflows/useWorkflowSections";

const CATEGORY_LABEL_KEYS: Record<WorkflowCategory, string> = {
  interoperability: "workflows.category.interoperability",
  modeling: "workflows.category.modeling",
  domain: "workflows.category.domain",
};

export default function WorkflowsPane({
  agent,
  list,
  enabledIds,
  onEnable,
  projectSelected,
  onEnter,
}: {
  agent: AgentTarget;
  list: WorkflowListState;
  enabledIds: readonly string[];
  onEnable: (id: string) => void;
  projectSelected: boolean;
  /** Enter an open workflow (by code): its view, on a new conversation. */
  onEnter: (workflowCode: string) => void;
}) {
  const { t, locale } = useLocale();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState(false);
  const [category, setCategory] = useState<WorkflowCategory | "all">("all");
  const [sort, setSort] = useState<WorkflowSort>(DEFAULT_WORKFLOW_SORT);

  const listed = list.kind === "ok" ? list.workflows : [];
  const pending = listed.find((item) => item.id === pendingId);
  const closeConfirmation = () => { setPendingId(null); setSaveError(false); };
  // Only the categories some card is in: an empty choice would filter to nothing.
  const usedCategories = WORKFLOW_CATEGORIES.filter((id) => listed.some((item) => item.category === id));
  const isOpen = (item: (typeof listed)[number]) => openWorkflow(item.code, listed) !== null;
  const cards = listWorkflows(listed, { query: "", category, sort }, (item) => workflowText(item, locale), isOpen);

  let body;
  if (list.kind === "loading") {
    body = <p className="workflows-empty">{t("chat.loading")}</p>;
  } else if (list.kind === "unsupported") {
    body = <p className="workflows-empty">{t("workflows.unsupported")}</p>;
  } else if (list.kind === "unavailable") {
    body = <p className="workflows-empty">{agent.ok ? t("workflows.unavailable") : describeAgent(agent)}</p>;
  } else if (cards.length === 0) {
    body = <p className="workflows-empty">{t("workflows.empty")}</p>;
  } else {
    body = (
      <div className="workflows-grid">
        {cards.map((card) => {
          const open = isOpen(card);
          const enabled = enabledIds.includes(card.id);
          const { title, description } = workflowText(card, locale);
          // What stands between the user and Start, in the order it would be met.
          const note =
            card.state === "coming-soon" ? null
            : !open ? t("workflows.unsupported")
            : !enabled ? null
            : !projectSelected ? t("projects.noneSelected")
            : null;
          return (
            <button
              key={card.code}
              type="button"
              className="workflow-card"
              data-workflow={card.code}
              data-state={card.state}
              disabled={!open || (enabled && !projectSelected)}
              onClick={() => {
                if (enabled) onEnter(card.code);
                else setPendingId(card.id);
              }}
            >
              <span className="workflow-card-head">
                <span className="workflow-card-category">{t(CATEGORY_LABEL_KEYS[card.category])}</span>
                {card.state === "coming-soon" ? <span className="workflow-card-tag">{t("workflows.comingSoon")}</span> : null}
              </span>
              <span className="workflow-card-title">{title}</span>
              <span className="workflow-card-desc">{description}</span>
              {note ? <span className="workflow-card-note">{note}</span> : null}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <section className="workflows-pane" data-pane="workflows">
      <div className="pane-head">
        <div className="pane-head-title">
          <h2>
            {t("workflows.title")}
            <span className="app-beta-badge">Beta</span>
          </h2>
        </div>
      </div>
      {/* Sort and filter on the left, a new workflow on the right — Coming soon,
          like the cards that are. */}
      <div className="workflows-toolbar">
        <div className="workflows-toolbar-start">
          <select
            className="workflows-select"
            value={sort}
            onChange={(event) => setSort(event.target.value as WorkflowSort)}
            aria-label={t("workflows.sort.label")}
          >
            <option value="category">{t("workflows.sort.category")}</option>
            <option value="available">{t("workflows.sort.available")}</option>
            <option value="name">{t("workflows.sort.name")}</option>
          </select>
          <select
            className="workflows-select"
            value={category}
            onChange={(event) => setCategory(event.target.value as WorkflowCategory | "all")}
            aria-label={t("workflows.filter.label")}
          >
            <option value="all">{t("workflows.filter.all")}</option>
            {usedCategories.map((id) => (
              <option key={id} value={id}>
                {t(CATEGORY_LABEL_KEYS[id])}
              </option>
            ))}
          </select>
        </div>
        <div className="workflows-toolbar-end">
          <span className="workflow-card-tag">{t("workflows.comingSoon")}</span>
          <button type="button" className="settings-btn secondary workflows-new" disabled title={t("workflows.comingSoon")}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
            {t("workflows.new")}
          </button>
        </div>
      </div>
      {body}
      <Modal
        open={pending !== undefined && isOpen(pending)}
        onClose={closeConfirmation}
        title={t("workflows.beta.enableTitle", { name: pending ? workflowText(pending, locale).title : "" })}
        footer={<>
          <button type="button" className="settings-btn secondary" autoFocus onClick={closeConfirmation}>
            {t("common.cancel")}
          </button>
          <button type="button" className="settings-btn" onClick={() => {
            if (!pending || !isOpen(pending)) return;
            try { onEnable(pending.id); }
            catch { setSaveError(true); return; }
            closeConfirmation();
            if (projectSelected) onEnter(pending.code);
          }}>
            {t("workflows.beta.enable")}
          </button>
        </>}
      >
        <p>{t("workflows.beta.warning")}</p>
        {saveError ? <p role="alert" className="settings-msg err">{t("settings.beta.saveFailed")}</p> : null}
      </Modal>
    </section>
  );
}
